import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getNewsById, getFilmById, getAllNews } from '@/lib/catalog';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function NewsDetail() {
  const { id } = useParams();
  const item = getNewsById(id);
  const relatedFilm = item ? getFilmById(item.relatedFilmId) : undefined;

  usePageMeta({
    title: item
      ? `${item.title} | 神宫寺奈绪影视内容站`
      : '新闻不存在 - 神宫寺奈绪影视内容站',
    description: item
      ? item.summary
      : '该新闻不存在或已被移除，请返回资讯列表浏览其他内容。',
    keywords: '神宫寺奈绪,娱乐资讯,明星动态',
  });

  if (!item) {
    return (
      <div className="px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-lg mx-auto text-center bg-background-100 border border-background-200 rounded-lg p-10">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-file-unknow-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">新闻不存在</h1>
          <p className="mt-3 text-sm text-foreground-500">
            您访问的资讯可能已被移除，或链接有误。
          </p>
          <Link
            to="/news"
            className="mt-7 inline-flex px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium items-center gap-2 whitespace-nowrap transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const moreNews = getAllNews()
    .filter((n) => n.id !== item.id)
    .slice(0, 3);

  return (
    <div className="w-full">
      <section className="px-4 md:px-6 pt-8 md:pt-12 pb-6 bg-background-100 border-b border-background-200">
        <Breadcrumb
          items={[{ label: '首页', to: '/' }, { label: '资讯', to: '/news' }, { label: '正文' }]}
        />
      </section>

      <article className="px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs text-accent-600 font-medium flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {item.date}
          </span>
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
            {item.title}
          </h1>

          <div className="mt-6 rounded-lg overflow-hidden aspect-video bg-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} 神宫寺奈绪娱乐资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((para, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                {para}
              </p>
            ))}
          </div>

          {relatedFilm && (
            <div className="mt-10 p-5 bg-background-100 border border-background-200 rounded-lg flex items-center gap-4">
              <div className="w-16 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                <img
                  src={relatedFilm.cover}
                  alt={relatedFilm.title}
                  title={`${relatedFilm.title} 神宫寺奈绪影视作品`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">相关作品</p>
                <h3 className="mt-1 text-sm md:text-base font-medium text-foreground-950">
                  {relatedFilm.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500">
                  {relatedFilm.type} · {relatedFilm.year}
                </p>
              </div>
              <Link
                to={`/detail/${relatedFilm.id}`}
                className="ml-auto px-4 h-10 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm flex items-center gap-1.5 whitespace-nowrap transition-colors"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          )}

          <div className="mt-10">
            <Link
              to="/news"
              className="inline-flex px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium items-center gap-2 whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
          </div>
        </div>
      </article>

      {moreNews.length > 0 && (
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
              更多资讯
            </h2>
            <div className="space-y-3">
              {moreNews.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group flex items-center justify-between gap-4 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-accent-300 transition-colors cursor-pointer"
                >
                  <h3 className="text-sm md:text-base text-foreground-900 group-hover:text-accent-600 transition-colors line-clamp-1">
                    {n.title}
                  </h3>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{n.date}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}